import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { WINDOW } from '../../constants/window';
import { ApiServiceService } from '../../services/api-service.service';
import {
  ISaveSurveyPayload,
  ISubmitSurveyPayload,
} from './multi-section-form-preview.models';
import { IRiskFormAssignmentRequest, IRiskFormAssignmentResponse } from '../scores-analysis/scores-analysis.models';

@Injectable({
  providedIn: 'root',
})
export class MultiSectionFormPreviewApiService {
  constructor(@Inject(WINDOW) private _window: Window) { }

  private _apiService = inject(ApiServiceService);
  private _httpClient = inject(HttpClient);


  // private _headers = new HttpHeaders()
  //   .set('Authorization', this._apiService.FormsAPIToken)
  //   .set('Access-Control-Allow-Origin', this._apiService.FormAPIAppID)
  //   .set('__RequestVerificationToken', this._apiService.FormsAPIToken);


  public getConditionalLogicJSON(templateId: string | number): Observable<any> {
    const apiUrl = `${this._apiService.dynamicEndPoint}/GetConditonalJson`;
    return this._httpClient.get<any>(`${apiUrl}/${templateId}`)
      .pipe(
        map(response => this._apiService.convertTopLevelKeysToPascalCase(response))
      );
  }

  public getUserAnswers(surveyId: string | number): Observable<any> {
    const apiUrl = `${this._apiService.dynamicEndPoint}/GetUserResponse`;
    return this._httpClient.get<any>(
      `${apiUrl}?surveyId=${surveyId}`)
      .pipe(
        map(response => this._apiService.convertKeysToPascalCase(response))
      );
  }

  public getSupplierUsers(): Observable<any> {
    // SM-3361: Use tenant + explicit Supplier path instead of dynamicEndPoint
    // GetSupplierUsers only exists in Supplier's FWClientController, not Buyer's
    // Using dynamicEndPoint in Review mode would call /Buyer/FWClient/GetSupplierUsers (404)
    const apiUrl = `${this._apiService.tenant ?? ''}/Supplier/FWClient/GetSupplierUsers`;
    return this._httpClient.get<any>(`${apiUrl}`)
      .pipe(
        map(response => this._apiService.convertKeysToPascalCase(response))
      );
  }

  public saveUserAnswers(userAnswersObj: ISaveSurveyPayload): Observable<any> {
    const apiUrl = `${this._apiService.dynamicEndPoint}/SaveUserSurveyResponse`;

    return this._httpClient.post<any>(
      `${apiUrl}`,
      userAnswersObj
    )
    .pipe(
      map(response => this._apiService.convertKeysToPascalCase(response))
    );
    
  }

  public submitUserAnswers(
    userAnswersObj: ISubmitSurveyPayload
  ): Observable<any> {
    const apiUrl = `${this._apiService.dynamicEndPoint}/SubmitUserSurveyResponse`;

    return this._httpClient.post<any>(
      `${apiUrl}`,
      userAnswersObj,
    ).pipe(
      map(response => this._apiService.convertKeysToPascalCase(response))
    );
  }

  public getCQScoresByJSON(questionnaire): Observable<any> {
    const apiUrl = `${this._apiService.dynamicEndPoint}/GetScoresforStandardForms`;

    // Response is consumed as camelCase (success/msg/data, compliance/risk/response,
    // label/score). Do NOT run convertKeysToPascalCase here or the keys flip to PascalCase.
    return this._httpClient.post<any>(
      `${apiUrl}`,
      questionnaire
    );
  }

  public hasLinkedForm(baseFormId: string): Observable<any> {
    const apiUrl = `${this._apiService.tenant ?? ''}/Supplier/ReviewSubmit/HasLinkedForm/${baseFormId}`;
    return this._httpClient.get<any>(`${apiUrl}`)
      .pipe(
        map(response => this._apiService.convertKeysToPascalCase(response))
      );
  }

  public assignRiskForm(request: IRiskFormAssignmentRequest): Observable<IRiskFormAssignmentResponse> {
    const apiUrl = `${this._apiService.tenant ?? ''}/Supplier/FormRequest/AssignRiskForm`;
    return this._httpClient.post<IRiskFormAssignmentResponse>(
      `${apiUrl}`,
      request,
    ).pipe(
      map(response => this._apiService.convertKeysToPascalCase(response))
    );
  }

  public validateEmail(email: string): Observable<{ IsValid: boolean; Message: string }> {
    const apiUrl = `${this._apiService.tenant ?? ''}/Supplier/FormRequest/ValidateEmail`;
    return this._httpClient.post<{ IsValid: boolean; Message: string }>(
      `${apiUrl}`,
      { email },
    ).pipe(
      map(response => this._apiService.convertKeysToPascalCase(response))
    );
  }
}
